import {
  createCourseOpenIntent,
  intentFromRestorePayload,
  normalizeOpenIntent,
} from '@/utils/openIntent';
import { useOpenIntentStore } from '@/stores/openIntentStore';
import { slimSessionPayload } from '@/utils/sessionPayloadSlim';
const STORAGE_KEY = 'calculationRestorePayload';

/**
 * @returns {object|null}
 */
export function readCalculationSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const normalized = { ...parsed };
    if (parsed.openIntent) {
      normalized.openIntent = normalizeOpenIntent(parsed.openIntent);
    } else {
      normalized.openIntent = intentFromRestorePayload(parsed);
    }
    return normalized;
  } catch {
    return null;
  }
}

/**
 * @param {object|null|undefined} payload
 */
export function writeCalculationSession(payload) {
  if (!payload || !payload.mode) return;
  try {
    const openIntent = normalizeOpenIntent(payload.openIntent) || intentFromRestorePayload(payload);
    const toStore = slimSessionPayload({ ...payload, openIntent });
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
  } catch {
    // ignore quota / private mode
  }
}

export function clearCalculationSession() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * @param {object} vm — страница расчёта с planStore и полями history*
 * @param {object} basePayload
 */
/**
 * @param {object|null|undefined} payload
 * @returns {string|null}
 */
export function planIdFromRestorePayload(payload) {
  if (!payload) return null;
  const direct = (payload.plan_id || '').trim();
  if (direct) return direct;
  const fromIntent = (payload.openIntent?.planId || '').trim();
  if (fromIntent) return fromIntent;
  const records = payload.runRecords || [];
  for (const rec of records) {
    const id = (rec?.plan_id || '').trim();
    if (id) return id;
  }
  return null;
}

/**
 * Минимальная сессия рабочего курса (план + модуль) для перезагрузки страницы.
 */
export function buildCourseWorkspaceSessionPayload({
  mode,
  planId,
  courseId,
  runRecords = [],
  openIntent = null,
  fractionation = null,
}) {
  const normalizedPlanId = (planId || '').trim() || null;
  const intent =
    normalizeOpenIntent(openIntent) ||
    (mode && (normalizedPlanId || courseId)
      ? createCourseOpenIntent({
          calculationModuleId: mode,
          courseId,
          planId: normalizedPlanId,
        })
      : null);
  const payload = {
    mode,
    plan_id: normalizedPlanId,
    openMode: null,
    run_id: null,
    runRecords: runRecords || [],
    openIntent: intent
      ? {
          ...intent,
          courseId: courseId ?? intent.courseId ?? null,
          planId: normalizedPlanId || intent.planId || null,
        }
      : null,
  };
  if (fractionation) payload.fractionation = fractionation;
  return payload;
}

export function persistCourseWorkspaceSession(payload) {
  if (!payload?.mode) return null;
  const planId = planIdFromRestorePayload(payload);
  if (!planId && !payload.openIntent?.courseId) return null;
  writeCalculationSession(payload);
  return payload;
}

export function isHistoryRestorePayload(payload) {
  return !!payload?.openMode;
}

export function buildPersistedCalculationPayload(vm, basePayload) {
  const planId =
    planIdFromRestorePayload(basePayload) || vm.planStore?.selectedPlanId || null;
  const existingIntent = normalizeOpenIntent(basePayload.openIntent) || intentFromRestorePayload(basePayload);
  return {
    ...basePayload,
    plan_id: planId,
    openMode: vm.historyOpenMode ?? basePayload.openMode ?? null,
    run_id: vm.runId ?? basePayload.run_id ?? null,
    source_run_id: vm.historySourceRunId ?? basePayload.source_run_id ?? null,
    source_created_at: vm.historySourceCreatedAt ?? basePayload.source_created_at ?? '',
    source_display_name: vm.historySourceDisplayName ?? basePayload.source_display_name ?? '',
    runRecords: basePayload.runRecords || [],
    openIntent: existingIntent
      ? {
          ...existingIntent,
          planId: planId || existingIntent.planId || null,
          runId: vm.runId ?? basePayload.run_id ?? existingIntent.runId ?? null,
        }
      : null,
  };
}

/**
 * @param {object} vm
 * @param {object} payload
 */
export function persistCalculationSession(vm, payload) {
  if (!payload?.mode) return null;
  if (payload.openMode) {
    const persisted = buildPersistedCalculationPayload(vm, payload);
    writeCalculationSession(persisted);
    return persisted;
  }
  const planId = planIdFromRestorePayload(payload) || vm.planStore?.selectedPlanId || null;
  const openIntent = payload.openIntent || useOpenIntentStore().intent;
  const courseId = openIntent?.courseId ?? null;
  const workspace = buildCourseWorkspaceSessionPayload({
    mode: payload.mode,
    planId,
    courseId,
    runRecords: payload.runRecords || [],
    openIntent,
    fractionation: payload.fractionation || null,
  });
  writeCalculationSession(workspace);
  return workspace;
}
